import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { subscriptionService } from '../services/subscription';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isEmailVerified: boolean;
  isPremium: boolean;
  subscriptionStatus: {
    status: 'trial' | 'premium' | 'expired';
    daysLeft: number;
  } | null;
  initializePayment: (amount: number) => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<AuthContextType['subscriptionStatus']>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      setIsEmailVerified(user.email_confirmed_at !== null);
      
      // Check subscription status
      const checkSubscription = async () => {
        try {
          if (!user.email) return;
          const status = await subscriptionService.checkSubscriptionStatus(user.email);
          setSubscriptionStatus(status);
          setIsPremium(status.status === 'premium' && status.daysLeft > 0);
        } catch (error) {
          console.error('Error checking subscription:', error);
        }
      };

      checkSubscription();
    } else {
      setIsPremium(false);
      setSubscriptionStatus(null);
    }
  }, [user]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ 
        email, 
        password
      });
      
      if (error) {
        console.error('Auth error:', error);
        throw error;
      }
    } catch (error) {
      console.error('Sign in error:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to authentication service. Please check your internet connection.');
      }
      throw error;
    }
  };

  const signUp = async (email: string, password: string, firstName: string) => {
    const { error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: {
          firstName: firstName
        }
      }
    });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/login');
    } catch (error) {
      throw error;
    }
  };

  const sendVerificationEmail = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user?.email || '',
        options: {
          emailRedirectTo: `${window.location.origin}/login`
        }
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const initializePayment = async (amount: number): Promise<string> => {
    if (!user?.email) {
      throw new Error('User email not found');
    }
    return subscriptionService.initializePayment(user.email, amount);
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    sendVerificationEmail,
    resetPassword,
    isEmailVerified,
    isPremium,
    subscriptionStatus,
    initializePayment
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};